import { END_POINT, appName } from "config/environment";
import linkedinIcon from "assets/images/linkedin.png";
import indeedIcon from "assets/images/indeed.png";
import glassdoorIcon from "assets/images/glassdoor.png";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { openNotification } from "utils/notification";
import { useEffect, useState } from "react";
import { BeatLoader } from "react-spinners";
import { Button, Checkbox, Switch } from "antd";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import {
  SocialPlatform,
  useSocialPreferences,
  useUpdateSocialPreferences,
} from "features/jobs/hooks/useSocialAutoPost";

interface LoadingState {
  [key: string]: boolean;
}

export const JobBoardSettings = () => {
  const { token } = useGetUserInfo();
  const { data: socialPreferences, isLoading: loadingPrefs } =
    useSocialPreferences(token);
  const { mutate: saveSocialPrefs, isLoading: savingPrefs } =
    useUpdateSocialPreferences();

  const [loading, setLoading] = useState<LoadingState>({
    linkedin: false,
    indeed: false,
    glassdoor: false,
  });
  const [autoPostDefault, setAutoPostDefault] = useState(false);
  const [defaultPlatforms, setDefaultPlatforms] = useState<SocialPlatform[]>([
    "linkedin",
    "facebook",
    "x",
  ]);

  useEffect(() => {
    if (!socialPreferences) return;
    setAutoPostDefault(socialPreferences.auto_post_enabled_by_default);
    setDefaultPlatforms(
      socialPreferences.default_platforms?.length
        ? socialPreferences.default_platforms
        : ["linkedin", "facebook", "x"],
    );
  }, [socialPreferences]);

  const redirectToPlatform = (platform: string) => {
    setLoading((prev) => ({ ...prev, [platform]: true }));
    fetch(`${END_POINT.AUTH_URL}/jboauth/${platform}`, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        setLoading((prev) => ({ ...prev, [platform]: false }));
        openNotification({
          title: "Error",
          state: "error",
          description: "Something went wrong!",
          duration: 4.5,
        });
        throw new Error("Something went wrong!");
      })
      .then((data) => window.location.assign(data.url))
      .catch((error) => console.error(error));
  };

  const handleSaveSocialDefaults = () => {
    if (!token) return;
    saveSocialPrefs(
      {
        token,
        auto_post_enabled_by_default: autoPostDefault,
        default_platforms: defaultPlatforms,
      },
      {
        onSuccess: () => {
          openNotification({
            title: "Saved",
            state: "success",
            description: "Arno social auto-post defaults updated.",
            duration: 4,
          });
        },
        onError: () => {
          openNotification({
            title: "Error",
            state: "error",
            description: "Could not save social defaults.",
            duration: 4.5,
          });
        },
      },
    );
  };

  const providers = socialPreferences?.providers;

  return (
    <DashboardLayout>
      <Navbar title="Job Board Settings" description="" />
      <div className="container mx-auto px-4 py-6 space-y-10">
        <section className="border rounded-md p-5 bg-white">
          <h1 className="text-xl font-semibold text-accent">
            Arno social auto-post defaults
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Control whether newly published jobs default to posting on Arno&apos;s
            LinkedIn organization page, Facebook Page, and X account. Per-job
            overrides are available on the publish preview step.
          </p>

          <div className="mt-5 flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-gray-800">
                Auto-post enabled by default
              </div>
              <p className="text-sm text-gray-500">
                Recommended off until LinkedIn/Meta provider access is approved.
              </p>
            </div>
            <Switch
              checked={autoPostDefault}
              loading={loadingPrefs}
              onChange={setAutoPostDefault}
            />
          </div>

          <div className="mt-4">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Default platforms
            </div>
            <Checkbox.Group
              value={defaultPlatforms}
              onChange={(values) =>
                setDefaultPlatforms(values as SocialPlatform[])
              }
              options={[
                { label: "LinkedIn", value: "linkedin" },
                { label: "Facebook", value: "facebook" },
                { label: "X", value: "x" },
              ]}
            />
          </div>

          <div className="mt-4 text-xs text-gray-500 space-y-1">
            <div>
              Feature flag:{" "}
              {providers?.feature_enabled ? "enabled" : "disabled"}
              {providers?.kill_switch ? " (kill switch on)" : ""}
            </div>
            <div>
              LinkedIn provider:{" "}
              {providers?.platforms?.linkedin ? "ready" : "not ready"}
            </div>
            <div>
              Facebook provider:{" "}
              {providers?.platforms?.facebook ? "ready" : "not ready"}
            </div>
            <div>
              X provider: {providers?.platforms?.x ? "ready" : "not ready"}
            </div>
          </div>

          <div className="mt-5">
            <Button
              type="primary"
              onClick={handleSaveSocialDefaults}
              loading={savingPrefs}
            >
              Save social defaults
            </Button>
          </div>
        </section>

        <section>
          <div className="text-center mb-6">
            <h1 className="text-xl font-semibold text-accent">
              Authorize Job Board Access
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Connect your LinkedIn, Indeed, or Glassdoor account to post jobs
              seamlessly from {appName}. This is separate from Arno company
              social auto-post.
            </p>
          </div>
          <div className="space-y-6">
            {[
              { name: "LinkedIn", key: "linkedin", icon: linkedinIcon },
              { name: "Indeed", key: "indeed", icon: indeedIcon },
              { name: "Glassdoor", key: "glassdoor", icon: glassdoorIcon },
            ].map(({ name, key }) => (
              <div
                key={key}
                className="flex items-center justify-between p-4 border rounded-md shadow-sm hover:shadow-lg transition duration-300 ease-in-out"
              >
                <div className="flex items-center space-x-4">
                  <div>
                    <h3 className="text-md font-semibold">{name}</h3>
                    <p className="text-sm text-gray-600">
                      Click on the {name} button to authorize
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => redirectToPlatform(key)}
                  className="flex items-center justify-center px-4 py-2 border border-slate-300 rounded bg-white text-accent hover:bg-accent hover:text-white transition duration-200"
                >
                  {loading[key] ? (
                    <BeatLoader color="#222" size={10} />
                  ) : (
                    "Authorize"
                  )}
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};
